import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { CountryModelDto } from '@treasury/api/channel';
import { CountriesService } from '@treasury/domain/utilities';
import { Bank, WireBeneficiary, WireCreditor, WireIsoService } from '@treasury/domain/wires';
import { LabeledList, TmContainer } from '@treasury/presentation';
import { html, nothing, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

export const tagName = 'creditor-data-readonly';
@customElement(tagName)
export class CreditorDataReadonly extends TmContainer {
    @InjectProperty()
    declare private wireIsoService: WireIsoService;

    @InjectProperty()
    declare private countriesService: CountriesService;

    @property({ type: Object })
    private creditor?: WireBeneficiary | WireCreditor;

    @state()
    private address?: string[];

    @state()
    private bank: Bank = new Bank();

    @state()
    private wireIsoLabels = WireIsoService.legacyLabels;

    @state()
    private countries: CountryModelDto[] = [];

    protected render() {
        if (!this.creditor) {
            return nothing;
        }
        return html`<div class="flex justify-between items-center px-6 pt-4">
                <div class="text-lg font-medium mb-1">${this.creditor.name}</div>
                ${this.creditor.status
                    ? html`<tm-status .status=${this.creditor.status}></tm-status>`
                    : nothing}
            </div>
            ${[this.renderCreditorDetail(), this.renderInstructedAgentInformation()]}`;
    }

    protected async firstUpdated() {
        [this.wireIsoLabels, this.countries] = await Promise.all([
            this.wireIsoService.getLabels(),
            this.countriesService.getAll(),
        ]);
    }

    protected updated(_changedProperties: PropertyValues): void {
        if (_changedProperties.has('creditor')) {
            this.bank = new Bank(this.creditor?.bank);
            this.address = this.creditor?.getIsoCompliantAddress
                ? this.creditor?.getIsoCompliantAddress(this.countries)
                : undefined;
        }
    }

    private renderCreditorDetail() {
        return html`<tm-labeled-list
            class="px-6 pb-4"
            .list=${new LabeledList(
                {
                    currency: this.creditor?.currencyType,
                    address: this.address,
                    accountNumber: this.creditor?.accountNumber,
                    agentId: this.bank.bankId,
                    agentName: this.bank.name,
                    agentAddress: this.bank.address,
                    notes: this.creditor?.notes,
                    audit: this.creditor?.audit,
                },
                [
                    'currency',
                    'address',
                    'accountNumber',
                    'agentId',
                    'agentName',
                    'agentAddress',
                    'notes',
                    'audit',
                ],
                {
                    agentId: this.wireIsoLabels.agentId,
                    agentName: `${this.wireIsoLabels.agent} Name`,
                    agentAddress: `${this.wireIsoLabels.agent} Address`,
                }
            )}
        ></tm-labeled-list>`;
    }

    private renderInstructedAgentInformation() {
        if (!this.creditor?.intermediaryBanks?.length) {
            return nothing;
        }
        const banks = this.creditor.intermediaryBanks.map((dto, i) => {
            const bank = new Bank(dto);
            return html`<div
                class=${classMap({
                    'mt-2 pt-2 border-t': i > 0,
                })}
            >
                ${i > 0
                    ? html`<div class="text-sm font-medium">
                          ${bank.isInternational ? `International` : `Domestic `} Intermediary
                          ${this.wireIsoLabels.agent}
                      </div>`
                    : nothing}
                <tm-labeled-list
                    .list=${new LabeledList(
                        {
                            agentCountry: bank.country,
                            agentId: bank.bankId,
                            agentName: bank.name,
                            agentAddress: bank.address,
                        },
                        ['agentCountry', 'agentId', 'agentName', 'agentAddress'],
                        {
                            agentCountry: `${this.wireIsoLabels.agent} Country`,
                            agentId: this.wireIsoLabels.agentId,
                            agentName: `${this.wireIsoLabels.agent} Name`,
                            agentAddress: `${this.wireIsoLabels.agent} Address`,
                        }
                    )}
                ></tm-labeled-list>
            </div>`;
        });
        return html`<div class="px-6 py-4 border-t-8">
            <div class="font-medium mb-1">Intermediary Agents</div>
            ${banks}
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: CreditorDataReadonly;
    }
}
